import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, ArrowRight } from "lucide-react";
import { CITIES } from "../../lib/site";
import { track } from "../../lib/analytics";
import { getAttribution } from "../../lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECTS = ["Garage Floor", "Patio", "Commercial", "Polished Concrete", "Grind & Seal", "Other"];
const EMPTY = { project: "", name: "", phone: "", email: "", address: "", city: "Prescott", preferred_date: "", details: "" };

const inputCls = "w-full bg-[#0f0f0f] border border-zinc-700 text-white placeholder-zinc-500 px-4 h-14 text-base focus:outline-none focus:border-[#C69453] transition-colors";
const labelCls = "block text-zinc-300 text-sm font-semibold mb-2";
const btnGold = "bg-[#C69453] hover:bg-[#AE7F44] text-black font-extrabold transition-colors";

const MetaLeadForm = ({ id = "estimate" }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const pickProject = (p) => {
    setForm({ ...form, project: p });
    setStep(2);
  };

  const nextFromContact = () => {
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please add your name, phone and email.");
      return;
    }
    setStep(3);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name, phone: form.phone, email: form.email, city: form.city,
        service_type: form.project || "Garage Floor", details: form.details, address: form.address,
        preferred_date: form.preferred_date,
        page_url: window.location.href, form_name: "meta_lp",
        submitted_at: new Date().toISOString(), ...getAttribution(),
      });
      track("generate_lead", { service: form.project, city: form.city, value: 0, currency: "USD" });
      navigate("/lp/meta/thank-you", { state: { fromForm: true, name: form.name, service: form.project, city: form.city } });
    } catch (err) {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id={id} className="bg-[#141414] border-2 border-white shadow-2xl p-6 sm:p-8" data-testid="meta-lead-form-card">
      <div className="-mx-6 sm:-mx-8 -mt-6 sm:-mt-8 mb-5 bg-[#C69453] text-black text-center font-extrabold py-3 px-4 text-sm sm:text-base uppercase tracking-wide" data-testid="meta-form-offer-banner">
        Save Up to $700 Off Your Garage Floor
      </div>
      <h2 className="text-2xl sm:text-3xl font-light text-white">Claim Your Offer — Free Estimate</h2>
      <p className="text-zinc-400 text-sm mt-2">Tell us a little about your project and our team will reach out with the next steps.</p>

      {/* Progress */}
      <div className="mt-5">
        <div className="flex justify-between text-xs text-zinc-500 mb-2">
          <span>Step {step} of 3</span>
          <span>{step === 1 ? "Your project" : step === 2 ? "Contact info" : "A few details"}</span>
        </div>
        <div className="h-1.5 bg-zinc-800 overflow-hidden">
          <div className="h-full bg-[#C69453] transition-all duration-300" style={{ width: `${(step / 3) * 100}%` }} />
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-6 space-y-5">
        {step === 1 && (
          <div>
            <label className={labelCls}>What type of project are you looking to complete?</label>
            <div className="grid grid-cols-2 gap-2.5">
              {PROJECTS.map((p) => (
                <button
                  type="button"
                  key={p}
                  data-testid={`meta-project-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => pickProject(p)}
                  className={`px-3 py-4 text-sm font-semibold border transition-colors text-center ${form.project === p ? "bg-[#C69453] border-[#C69453] text-black" : "bg-[#0f0f0f] border-zinc-700 text-zinc-300 hover:border-[#C69453]"}`}
                >
                  {p}
                </button>
              ))}
            </div>
            <p className="text-zinc-600 text-xs mt-3">Tap a project to continue</p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div>
              <label className={labelCls}>Full Name *</label>
              <input name="name" value={form.name} onChange={onChange} placeholder="John Smith" data-testid="meta-form-name" className={inputCls} autoFocus />
            </div>
            <div>
              <label className={labelCls}>Phone *</label>
              <input name="phone" type="tel" value={form.phone} onChange={onChange} placeholder="(928) 555-0100" data-testid="meta-form-phone" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email *</label>
              <input name="email" type="email" value={form.email} onChange={onChange} placeholder="you@email.com" data-testid="meta-form-email" className={inputCls} />
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setStep(1)} data-testid="meta-step-back" className="flex items-center gap-1 text-zinc-400 hover:text-white px-3 py-3 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button type="button" onClick={nextFromContact} data-testid="meta-step-next" className={`${btnGold} flex-1 flex items-center justify-center gap-2 py-4 text-base`}>
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div>
              <label className={labelCls}>Property Address</label>
              <input name="address" value={form.address} onChange={onChange} placeholder="123 Main St" data-testid="meta-form-address" className={inputCls} autoFocus />
            </div>
            <div>
              <label className={labelCls}>Closest City</label>
              <select name="city" value={form.city} onChange={onChange} data-testid="meta-form-city" className={`${inputCls} cursor-pointer`}>
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Preferred Date</label>
              <input name="preferred_date" type="date" value={form.preferred_date} onChange={onChange} min={new Date().toISOString().split("T")[0]} data-testid="meta-form-preferred-date" className={`${inputCls} cursor-pointer`} />
            </div>
            <div>
              <label className={labelCls}>Project Details (optional)</label>
              <input name="details" value={form.details} onChange={onChange} placeholder="Approx. size, timing, color ideas..." data-testid="meta-form-details" className={inputCls} />
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setStep(2)} data-testid="meta-step-back-2" className="flex items-center gap-1 text-zinc-400 hover:text-white px-3 py-3 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button type="submit" disabled={submitting} data-testid="meta-form-submit" className={`${btnGold} flex-1 py-4 text-lg disabled:opacity-60`}>
                {submitting ? "Sending..." : "GET MY FREE ESTIMATE"}
              </button>
            </div>
            <p className="flex items-center justify-center gap-2 text-zinc-500 text-xs">
              <ShieldCheck className="w-4 h-4 text-[#C69453]" /> Free estimate • No obligation
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default MetaLeadForm;
